import React, { useState, useEffect } from 'react';
import { Container, Row, Col, Table, Button, Form, Pagination, InputGroup, Modal, Alert } from 'react-bootstrap';
import Sidebar from '../components/Sidebar';
import Header from '../components/Header';
import { API_ENDPOINTS } from '../config';
import axios from 'axios';
import { BsPencilSquare, BsTrash, BsSearch, BsSortDown, BsSortUp } from 'react-icons/bs';

const Plans = () => {
  const [plans, setPlans] = useState([]);
  const [editingPlan, setEditingPlan] = useState(null);
  const [showAddPlanModal, setShowAddPlanModal] = useState(false);
  const [formData, setFormData] = useState({
    price: '',
    months: '',
    description: ''
  });
  const [currentPage, setCurrentPage] = useState(1);
  const [plansPerPage] = useState(10);
  const [searchTerm, setSearchTerm] = useState('');
  const [sortField, setSortField] = useState('price');
  const [sortDirection, setSortDirection] = useState('asc');
  const [error, setError] = useState(null);

  useEffect(() => {
    fetchPlans();
  }, []);

  const fetchPlans = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(API_ENDPOINTS.listAllPlans, {
        headers: { token }
      });
      setPlans(response.data.plans);
    } catch (error) {
      console.error('Error fetching plans:', error);
      setError('Failed to fetch plans. Please try again later.');
    }
  };

  const sortPlans = (field) => {
    if (field === sortField) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortDirection('asc');
    }
  };

  const sortedAndFilteredPlans = plans
    .filter(plan =>
      plan.price.toString().includes(searchTerm.toLowerCase()) ||
      plan.months.toString().includes(searchTerm.toLowerCase()) ||
      plan.description.join(' ').toLowerCase().includes(searchTerm.toLowerCase())
    )
    .sort((a, b) => {
      if (a[sortField] < b[sortField]) return sortDirection === 'asc' ? -1 : 1;
      if (a[sortField] > b[sortField]) return sortDirection === 'asc' ? 1 : -1;
      return 0;
    });

  const indexOfLastPlan = currentPage * plansPerPage;
  const indexOfFirstPlan = indexOfLastPlan - plansPerPage;
  const currentPlans = sortedAndFilteredPlans.slice(indexOfFirstPlan, indexOfLastPlan);

  const paginate = (pageNumber) => setCurrentPage(pageNumber);

  const handleDeletePlan = async (planId) => {
    if (window.confirm('Are you sure you want to delete this plan?')) {
      try {
        const token = localStorage.getItem('token');
        await axios.delete(API_ENDPOINTS.deletePlan, {
          headers: { token, planId }
        });
        fetchPlans();
      } catch (error) {
        console.error('Error deleting plan:', error);
        setError('Failed to delete plan. Please try again.');
      }
    }
  };

  const handleEditPlan = (plan) => {
    setEditingPlan({ ...plan });
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setEditingPlan({
      ...editingPlan,
      [name]: value
    });
  };

  const handleEditSubmit = async (e) => {
    e.preventDefault();
  
    // Log all data for debugging
    console.log("Submitting edit with data:", editingPlan);
    console.log("Type of editingPlan.description:", typeof editingPlan.description);
    console.log("Content of editingPlan.description:", editingPlan.description);
  
    const endpoint = `${API_ENDPOINTS.editPlan}`;
    console.log("API endpoint URL:", endpoint);  // Log the full URL to confirm correctness
  
    try {
      const token = localStorage.getItem('token');
      const response = await axios.patch(endpoint, {
        price: editingPlan.price,
        months: editingPlan.months,
        description: Array.isArray(editingPlan.description)
          ? editingPlan.description
          : editingPlan.description.split('\n')
      }, {
        headers: {
          token,
          planId: editingPlan.planId // Send planId in headers
        }
      });
  
      console.log("Edit successful:", response.data);
      setEditingPlan(null);
      fetchPlans();
    } catch (error) {
      console.error("Error updating plan:", error);
      if (error.response) {
        // More detailed error logging
        console.error("Response data:", error.response.data);
        console.error("Response status:", error.response.status);
        console.error("Response headers:", error.response.headers);
      } else {
        console.error("Error message:", error.message);
      }
      setError('Failed to update plan. Please try again.');
    }
  };
  

  const handleAddPlanChange = (e) => {
    const { name, value } = e.target;
    setFormData({ ...formData, [name]: value });
  };

  const handleAddPlanSubmit = async (e) => {
    e.preventDefault();
    try {
      const token = localStorage.getItem('token');
      await axios.post(API_ENDPOINTS.addPlan, {
        price: formData.price,
        months: formData.months,
        description: formData.description.split('\n') // Split by newline to create an array
      }, {
        headers: { token }
      });
      fetchPlans();
      setShowAddPlanModal(false);
      setFormData({ price: '', months: '', description: '' });
    } catch (error) {
      console.error('Error creating plan:', error);
      setError('Failed to create plan. Please try again.');
    }
  };

  return (
    <div className="d-flex">
      <Sidebar style={{ position: 'fixed', top: 0, bottom: 0, left: 0, width: '250px', zIndex: 1000 }} />
      <div style={{ width: 'calc(100%)', height: 'calc(100vh)', overflowY: 'auto' }}>
        <Header style={{ position: 'fixed', top: 0, left: '250px', width: 'calc(100% - 250px)', zIndex: 1000 }} />
        <Container fluid className="mt-3">
          <Row>
            <Col>
              {error && <Alert variant="danger" onClose={() => setError(null)} dismissible>{error}</Alert>}
              <div className="d-flex justify-content-between align-items-center mb-3">
                <h2>Plans</h2>
                <Button variant="primary" onClick={() => setShowAddPlanModal(true)}>
                  Add Plan
                </Button>
              </div>
              <InputGroup className="mb-3">
                <InputGroup.Text><BsSearch /></InputGroup.Text>
                <Form.Control
                  placeholder="Search plans..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                />
              </InputGroup>
              <Table striped bordered hover>
                <thead>
                  <tr>
                    <th onClick={() => sortPlans('price')}>
                      Price {sortField === 'price' && (sortDirection === 'asc' ? <BsSortUp /> : <BsSortDown />)}
                    </th>
                    <th onClick={() => sortPlans('months')}>
                      Months {sortField === 'months' && (sortDirection === 'asc' ? <BsSortUp /> : <BsSortDown />)}
                    </th>
                    <th onClick={() => sortPlans('description')}>
                      Description {sortField === 'description' && (sortDirection === 'asc' ? <BsSortUp /> : <BsSortDown />)}
                    </th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {currentPlans.map((plan) => (
                    <tr key={plan._id}>
                      {editingPlan && editingPlan._id === plan._id ? (
                        <>
                          <td>
                            <Form.Control
                              type="text"
                              name="price"
                              value={editingPlan.price}
                              onChange={handleChange}
                            />
                          </td>
                          <td>
                            <Form.Control
                              type="text"
                              name="months"
                              value={editingPlan.months}
                              onChange={handleChange}
                            />
                          </td>
                          <td>
                            <Form.Control
                              as="textarea"
                              rows={3}
                              name="description"
                              value={editingPlan.description.join('\n')}
                              onChange={handleChange}
                            />
                          </td>
                          <td>
                            <Button variant="primary" onClick={handleEditSubmit}>Save</Button>{' '}
                            <Button variant="secondary" onClick={() => setEditingPlan(null)}>Cancel</Button>
                          </td>
                        </>
                      ) : (
                        <>
                          <td>{plan.price}</td>
                          <td>{plan.months}</td>
                          <td>{plan.description.join(', ')}</td>
                          <td>
                            <Button variant="warning" onClick={() => handleEditPlan(plan)}><BsPencilSquare /></Button>{' '}
                            <Button variant="danger" onClick={() => handleDeletePlan(plan.planId)}><BsTrash /></Button>
                          </td>
                        </>
                      )}
                    </tr>
                  ))}
                </tbody>
              </Table>
              <Pagination className="mt-3">
                {[...Array(Math.ceil(plans.length / plansPerPage)).keys()].map((number) => (
                  <Pagination.Item key={number} onClick={() => paginate(number + 1)} active={number + 1 === currentPage}>
                    {number + 1}
                  </Pagination.Item>
                ))}
              </Pagination>
            </Col>
          </Row>
          <Modal show={showAddPlanModal} onHide={() => setShowAddPlanModal(false)}>
            <Modal.Header closeButton>
              <Modal.Title>Add Plan</Modal.Title>
            </Modal.Header>
            <Modal.Body>
              <Form onSubmit={handleAddPlanSubmit}>
                <Form.Group controlId="price">
                  <Form.Label>Price</Form.Label>
                  <Form.Control type="number" name="price" value={formData.price} onChange={handleAddPlanChange} required />
                </Form.Group>
                <Form.Group controlId="months">
                  <Form.Label>Months</Form.Label>
                  <Form.Control type="number" name="months" value={formData.months} onChange={handleAddPlanChange} required />
                </Form.Group>
                <Form.Group controlId="description">
                  <Form.Label>Description</Form.Label>
                  <Form.Control as="textarea" rows={3} name="description" value={formData.description} onChange={handleAddPlanChange} required />
                </Form.Group>
                <Button variant="primary" type="submit" className="mt-3">Add Plan</Button>
              </Form>
            </Modal.Body>
          </Modal>
        </Container>
      </div>
    </div>
  );
};

export default Plans;
